<template>
	<ArrowImageGallery
		v-if="images?.length > 1 && bigImageIndexRef"
		arrow-direction="left"
		:class="[`${showPopUp && 'ml-4'}`]"
		:number-of-slides="images?.length"
		is-bg
		@click="decreaseBigImageIndex(bigImageIndexRef - 1)"
	/>
	<div
		ref="bigPictureCarouselRef"
		class="flex overflow-x-auto md:no-scrollbar h-full items-center"
	>
		<div
			v-for="(image, index) in images"
			:id="`${!showPopUp && 'bigImagesGallery'}`"
			:key="imageId(image) + size"
			ref="bigImagesGallery"
			:class="[
				`flex ${size ? 'min-w-full' : 'min-w-[568px]'} relative max-h-full `
			]"
		>
			<div
				:ref="
					(el) => {
						imagesRef[index] = {
							[size]: el as HTMLElement
						};
					}
				"
				:class="[`${zoomedInPopUpPictureWrapper} w-[100%] justify-center `]"
			>
				<div
					v-show="showPopUp"
					:class="[
						` ${zoomedInPopUpPictureChild} flex min-w-[90%] h-full absolute left-[50%] -translate-x-[50%]`
					]"
					@click="doAction"
				></div>
				<div
					v-show="!showPopUp"
					class="flex absolute right-2.5 bottom-2.5 cursor-pointer transition-transform hover:scale-110"
					data-testid="product-gallery-zoom"
					@click="doAction"
				>
					<ArrowsPointingOutIcon class="w-8 h-8" />
				</div>
				<!-- Show big image on zoom -->
				<NuxtImg
					v-if="showPopUp"
					:src="`${imagePath(image)}?format=webp`"
					:alt="$t('Image №{value}', { value: index + 1 })"
					:loading="index != 0 ? 'lazy' : 'eager'"
					:class="`  h-auto w-auto  object-center`"
					sizes="100vw"
					:placeholder="
						img(`${imagePath(image)}?format=webp`, {
							h: 10,
							f: 'webp',
							blur: 2,
							q: 50
						})
					"
				/>
				<!-- Show small image otherwise -->
				<NuxtImg
					v-else
					:src="`${imagePath(image)}?format=webp&w=570`"
					:alt="$t('Image №{value}', { value: index + 1 })"
					:loading="index != 0 ? 'lazy' : 'eager'"
					:class="` object-cover h-full w-full  object-center md:h-full md:w-full`"
					sizes="100vw"
					:placeholder="
						img(`${imagePath(image)}?format=webp&w=570`, {
							h: 10,
							f: 'webp',
							blur: 2,
							q: 50
						})
					"
					width="500"
					height="500"
				/>
			</div>
		</div>
	</div>
	<ArrowImageGallery
		v-if="images?.length > 1 && bigImageIndexRef < images?.length - 1"
		:number-of-slides="images?.length"
		arrow-direction="right"
		:class="[`${showPopUp && 'mr-4'}`]"
		is-bg
		@click="increaseBigImageIndex(bigImageIndexRef + 1)"
	/>
</template>

<script setup lang="ts">
import { toRef } from "vue";
import { useResizeObserver } from "@vueuse/core";
import type { ProductFile } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { CategoryFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { FilteredProductItemFiles } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	SetShowPopUp,
	BigImageIndex
} from "~/types/storyblok/imageGallery/imageGallery";
import { ArrowsPointingOutIcon } from "@heroicons/vue/24/outline";

const img = useImage();

interface Props {
	images: ProductFile[] | CategoryFiles[] | FilteredProductItemFiles[];
	bigImageIndex: number;
	decreaseBigImageIndex: BigImageIndex;
	increaseBigImageIndex: BigImageIndex;
	size: string;
	setShowPopUp?: SetShowPopUp;
	showPopUp?: boolean;
}

const {
	images,
	bigImageIndex,
	decreaseBigImageIndex,
	increaseBigImageIndex,
	size,
	setShowPopUp,
	showPopUp
} = defineProps<Props>();

const bigImageIndexRef = toRef(() => bigImageIndex);
const bigImagesGallery = ref<HTMLElement[]>();
const imagesRef = ref<{ [key: string]: HTMLElement }[]>([]);
const bigPictureCarouselRef = ref<HTMLElement>();

const zoomedInPopUpPictureWrapper = ref("flex");
const zoomedInPopUpPictureChild = ref("cursor-zoom-in");
const slideToImage = ({
	displayedBigImage,
	smooth
}: {
	displayedBigImage: number;
	smooth?: boolean;
}) => {
	if (Object.keys(imagesRef.value).length) {
		if (bigPictureCarouselRef.value) {
			const stepSize =
				imagesRef?.value?.[displayedBigImage]?.[size]?.getBoundingClientRect()
					?.width;
			if (smooth) {
				bigPictureCarouselRef.value.classList.add("scroll-smooth");
			}
			const scrollLeft = stepSize! * displayedBigImage;
			bigPictureCarouselRef.value.scrollLeft = scrollLeft;
		}
	}
};

onMounted(() => {
	slideToImage({ displayedBigImage: bigImageIndex });
});

const doAction = () => {
	if (!showPopUp) {
		if (setShowPopUp) {
			setShowPopUp(true);
		}
		zoomedInPopUpPictureWrapper.value = "";

		const body = document.getElementsByTagName("body")[0];
		if (body) {
			body.style.overflow = "hidden";
		}

		zoomedInPopUpPictureChild.value = "max-h-[2600px] cursor-zoom-out";
	} else {
		if (zoomedInPopUpPictureWrapper.value === "flex") {
			zoomedInPopUpPictureChild.value = "max-h-[2600px] cursor-zoom-out";

			return (zoomedInPopUpPictureWrapper.value = "");
		} else {
			zoomedInPopUpPictureWrapper.value = "flex";
			zoomedInPopUpPictureChild.value = "cursor-zoom-in";
		}
	}
};

const emits = defineEmits(["bigImagesGalleryRef"]);
watchEffect(() => {
	slideToImage({
		displayedBigImage: bigImageIndex,
		smooth: true
	});

	emits("bigImagesGalleryRef", { bigImagesGallery });
});

useResizeObserver(bigPictureCarouselRef, () => {
	slideToImage({
		displayedBigImage: bigImageIndex,
		smooth: true
	});
});

const { isCategoryFile, isFilteredProductItemFiles } = useNorcePwa();

const imagePath = (
	image: ProductFile | CategoryFiles | FilteredProductItemFiles
) => {
	return isCategoryFile(image) ? image?.ImagePath : image?.Path;
};

const imageId = (
	image: ProductFile | CategoryFiles | FilteredProductItemFiles
) => {
	return isCategoryFile(image) || isFilteredProductItemFiles(image)
		? image?.ImageKey
		: image?.Key;
};

useHead(() => {
	return {
		link: images?.[0]
			? [
					{
						rel: "preload",
						as: "image",
						href: `${imagePath(images[0])}?format=webp&w=570`,
						fetchpriority: "high"
					}
				]
			: []
	};
});
</script>
